import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "../api";
import { BRAND } from "../config/brand";
import KOREA_REGIONS, {
  getDistance,
  getAllDistricts,
  getDistrictKey,
  getDistrictRegions,
  getQualifiedDistrictLabel,
  getRegionData,
  normalizeRegionKey,
  regionHasDistrict,
  splitQualifiedDistrict,
} from "../data/koreaDistricts";
import useScrollReveal from "../hooks/useScrollReveal";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { isAdminRole } from "../utils/roles";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";

const VWORLD_KEY = "CFDB3172-C1F9-3E4C-9E65-7D391C6F4548";
const VWORLD_BASE = `https://api.vworld.kr/req/wmts/1.0.0/${VWORLD_KEY}/Base/{z}/{y}/{x}.png`;
const RENTAL_LOCATION_STORAGE = "ik_rental_location";
const NATIONWIDE_REGION = "전국";

const safeDecode = (value) => {
  try {
    return decodeURIComponent(value || "");
  } catch {
    return value || "";
  }
};

/* Map fly-to helper */
function MapFlyTo({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, zoom || map.getZoom(), { duration: 0.6 });
  }, [center, zoom, map]);
  return null;
}

export default function RegionalRentalPage() {
  const { region: urlRegion, district: urlDistrict } = useParams();
  const navigate = useNavigate();
  const revealRef = useScrollReveal();
  const { t } = useI18n();
  const { user } = useAuth();
  const isAdmin = isAdminRole(user?.role);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [districtCounts, setDistrictCounts] = useState({});
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showRentalMap, setShowRentalMap] = useState(false);
  const [mapFlyCenter, setMapFlyCenter] = useState(null);
  const [mapFlyZoom, setMapFlyZoom] = useState(null);

  /* ── Filters ── */
  const [filterColor, setFilterColor] = useState("");   // "color" | "mono" | ""
  const [filterBrand, setFilterBrand] = useState("");
  const [filterSort, setFilterSort] = useState("");      // "price_asc" | "price_desc" | ""
  const [filterCategory, setFilterCategory] = useState("");
  const [availableFilters, setAvailableFilters] = useState({ brands: [], categories: [] });
  const [adminOrderMode, setAdminOrderMode] = useState(false);
  const [adminOrderProducts, setAdminOrderProducts] = useState([]);
  const [adminOrderDirty, setAdminOrderDirty] = useState(false);
  const [adminOrderSavingKey, setAdminOrderSavingKey] = useState("");
  const [adminOrderDragId, setAdminOrderDragId] = useState(null);
  const [adminOrderToast, setAdminOrderToast] = useState("");
  const isNationwideRental = selectedRegion === NATIONWIDE_REGION;

  /* ── Load available filters ── */
  useEffect(() => {
    api.get("/api/product-filters").then(r => setAvailableFilters(r.data)).catch(() => {});
  }, []);

  /* ── Geolocation ── */
  const [userLocation, setUserLocation] = useState(null);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [nearestDistrict, setNearestDistrict] = useState(null);
  const [userRegionLabel, setUserRegionLabel] = useState("");
  const activeDistrictRef = useRef(null);
  const restoredLocationRef = useRef(false);

  /* ── Restore my-location choice within the session ── */
  useEffect(() => {
    if (restoredLocationRef.current) return;
    restoredLocationRef.current = true;
    const raw = sessionStorage.getItem(RENTAL_LOCATION_STORAGE);
    if (!raw) return;
    try {
      const saved = JSON.parse(raw);
      if (saved.loc) setUserLocation(saved.loc);
      if (saved.nearest) setNearestDistrict(saved.nearest);
      if (saved.label) setUserRegionLabel(saved.label);
      if (!urlRegion && !urlDistrict && saved.nearest?.regionShort && saved.nearest?.name) {
        setSelectedRegion(saved.nearest.regionShort);
        setSelectedDistrict(saved.nearest.name);
        navigate(
          `/rental/${encodeURIComponent(saved.nearest.regionShort)}/${encodeURIComponent(saved.nearest.name)}`,
          { replace: true }
        );
      }
    } catch {
      sessionStorage.removeItem(RENTAL_LOCATION_STORAGE);
    }
  }, [urlRegion, urlDistrict, navigate]);

  /* ── Resolve URL params on mount ── */
  useEffect(() => {
    const moveToRentalHome = () => {
      setSelectedRegion("");
      setSelectedDistrict("");
      navigate("/rental", { replace: true });
    };

    if (urlDistrict) {
      const decodedRegion = normalizeRegionKey(safeDecode(urlRegion));
      const parsedDistrict = splitQualifiedDistrict(safeDecode(urlDistrict));
      const districtName = parsedDistrict.district || safeDecode(urlDistrict);
      const regionFromPath = decodedRegion || parsedDistrict.region;

      if (regionHasDistrict(regionFromPath, districtName)) {
        setSelectedRegion(normalizeRegionKey(regionFromPath));
        setSelectedDistrict(districtName);
        return;
      }

      const matchingRegions = getDistrictRegions(districtName);
      if (!regionFromPath && matchingRegions.length === 1) {
        setSelectedRegion(matchingRegions[0].short);
        setSelectedDistrict(districtName);
        navigate(
          `/rental/${encodeURIComponent(matchingRegions[0].short)}/${encodeURIComponent(districtName)}`,
          { replace: true }
        );
        return;
      }

      moveToRentalHome();
    } else if (urlRegion) {
      const decoded = safeDecode(urlRegion);
      if (decoded === NATIONWIDE_REGION) {
        setSelectedRegion(NATIONWIDE_REGION);
        setSelectedDistrict("");
        return;
      }
      const found = getRegionData(decoded);
      if (found) {
        setSelectedRegion(found.short);
        setSelectedDistrict("");
        return;
      }
      const matchingRegions = getDistrictRegions(decoded);
      if (matchingRegions.length === 1) {
        setSelectedRegion(matchingRegions[0].short);
        setSelectedDistrict(decoded);
        navigate(
          `/rental/${encodeURIComponent(matchingRegions[0].short)}/${encodeURIComponent(decoded)}`,
          { replace: true }
        );
      } else {
        moveToRentalHome();
      }
    } else {
      setSelectedRegion("");
      setSelectedDistrict("");
    }
  }, [urlRegion, urlDistrict, navigate]);

  /* ── Load district counts ── */
  useEffect(() => {
    api
      .get("/api/districts")
      .then((res) => {
        const map = {};
        (res.data || []).forEach((d) => {
          if (d.key) map[d.key] = d.count;
          if (d.region && d.district) map[getDistrictKey(d.region, d.district)] = d.count;
          if (d.district) map[d.district] = Math.max(map[d.district] || 0, d.count || 0);
        });
        setDistrictCounts(map);
      })
      .catch(() => {});
  }, []);

  /* ── Geolocation prompt ── */
  useEffect(() => {
    const asked = sessionStorage.getItem("ik_loc_asked");
    if (!asked && !urlDistrict && !urlRegion) {
      const t = setTimeout(() => setShowLocationPrompt(true), 1000);
      return () => clearTimeout(t);
    }
  }, [urlDistrict, urlRegion]);

  const requestLocation = () => {
    setShowLocationPrompt(false);
    sessionStorage.setItem("ik_loc_asked", "1");
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setUserLocation(loc);
        const all = getAllDistricts();
        let nearest = all[0],
          minDist = Infinity;
        all.forEach((d) => {
          const dist = getDistance(loc.lat, loc.lng, d.lat, d.lng);
          if (dist < minDist) {
            minDist = dist;
            nearest = d;
          }
        });
        setNearestDistrict(nearest);
        const label = `${nearest.regionShort} ${nearest.name}`;
        setUserRegionLabel(label);
        sessionStorage.setItem(RENTAL_LOCATION_STORAGE, JSON.stringify({
          loc,
          nearest: {
            name: nearest.name,
            regionShort: nearest.regionShort,
            lat: nearest.lat,
            lng: nearest.lng,
          },
          label,
        }));
        setSelectedRegion(nearest.regionShort);
        setSelectedDistrict(nearest.name);
        navigate(
          `/rental/${encodeURIComponent(nearest.regionShort)}/${encodeURIComponent(nearest.name)}`,
          { replace: true }
        );
      },
      () => sessionStorage.setItem("ik_loc_asked", "1"),
      { enableHighAccuracy: false, timeout: 8000 }
    );
  };

  const dismissLocation = () => {
    setShowLocationPrompt(false);
    sessionStorage.setItem("ik_loc_asked", "1");
  };

  /* ── Fetch products ── */
  /* ── Fetch products (cascading: 전국 + city + district) ── */
  const fetchProducts = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (selectedRegion && !isNationwideRental) params.set("region", selectedRegion);
    if (selectedDistrict) params.set("district", selectedDistrict);
    if (searchQuery) params.set("q", searchQuery);
    if (filterColor) params.set("color", filterColor);
    if (filterBrand) params.set("brand", filterBrand);
    if (filterSort) params.set("sort", filterSort);
    if (filterCategory) params.set("category", filterCategory);
    api
      .get(`/api/products-by-region?${params}`)
      .then((res) => setProducts(res.data || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [selectedRegion, selectedDistrict, searchQuery, filterColor, filterBrand, filterSort, filterCategory, isNationwideRental]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    setAdminOrderProducts(products);
    setAdminOrderDirty(false);
    setAdminOrderDragId(null);
  }, [products]);

  useEffect(() => {
    if (!adminOrderToast) return undefined;
    const tid = setTimeout(() => setAdminOrderToast(""), 3000);
    return () => clearTimeout(tid);
  }, [adminOrderToast]);

  /* ── Per-region/district SEO content (matches server-rendered bot view) ── */
  const [seoContent, setSeoContent] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedRegion && !isNationwideRental) params.append("region", selectedRegion);
    if (selectedDistrict) params.append("district", selectedDistrict);
    api.get(`/api/seo/rental?${params.toString()}`)
      .then((r) => setSeoContent(r.data))
      .catch(() => setSeoContent(null));
  }, [selectedRegion, selectedDistrict, isNationwideRental]);

  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  /* ── Navigation ── */
  const handleRegionClick = (regionShort) => {
    if (regionShort === NATIONWIDE_REGION) {
      setSelectedRegion(NATIONWIDE_REGION);
      setSelectedDistrict("");
      navigate(`/rental/${encodeURIComponent(NATIONWIDE_REGION)}`, { replace: true });
      if (window.innerWidth < 1024) setSidebarOpen(false);
      return;
    }
    if (selectedRegion === regionShort && !selectedDistrict) {
      setSelectedRegion("");
      navigate("/rental", { replace: true });
    } else {
      setSelectedRegion(regionShort);
      setSelectedDistrict("");
      navigate(`/rental/${encodeURIComponent(regionShort)}`, { replace: true });
    }
    if (window.innerWidth < 1024) setSidebarOpen(false);
  };

  const handleDistrictClick = (regionShort, distName) => {
    setSelectedRegion(regionShort);
    setSelectedDistrict(distName);
    navigate(
      `/rental/${encodeURIComponent(regionShort)}/${encodeURIComponent(distName)}`,
      { replace: true }
    );
    if (window.innerWidth < 1024) setSidebarOpen(false);
  };

  const handleAllClick = () => {
    setSelectedRegion("");
    setSelectedDistrict("");
    navigate("/rental", { replace: true });
  };

  const handleNationwideClick = () => {
    handleRegionClick(NATIONWIDE_REGION);
  };

  const handleAdminOrderToggle = () => {
    if (adminOrderMode && adminOrderDirty && !window.confirm("저장하지 않은 진열 순서 변경을 버릴까요?")) return;
    setAdminOrderProducts(products);
    setAdminOrderDirty(false);
    setAdminOrderDragId(null);
    setAdminOrderMode((v) => !v);
  };

  const moveAdminOrderProduct = (sectionItems, fromIndex, toIndex) => {
    if (fromIndex < 0 || toIndex < 0 || toIndex >= sectionItems.length || fromIndex === toIndex) return;
    const moving = sectionItems[fromIndex];
    const target = sectionItems[toIndex];
    if (!moving || !target) return;
    setAdminOrderProducts((prev) => {
      const next = [...prev];
      const fromGlobal = next.findIndex((item) => item.id === moving.id);
      if (fromGlobal < 0) return prev;
      const [pulled] = next.splice(fromGlobal, 1);
      const targetGlobal = next.findIndex((item) => item.id === target.id);
      if (targetGlobal < 0) return prev;
      next.splice(targetGlobal + (toIndex > fromIndex ? 1 : 0), 0, pulled);
      return next;
    });
    setAdminOrderDirty(true);
  };

  const saveAdminOrderSection = async (storeCategory, sectionItems) => {
    if (!storeCategory || !sectionItems.length) return;
    const key = storeCategory;
    setAdminOrderSavingKey(key);
    try {
      const { data: context } = await api.get("/api/admin/product-display-order", {
        params: { placement: "rental", store_category: storeCategory },
      });
      const visibleIds = sectionItems.map((p) => Number(p.id)).filter(Boolean);
      const visibleSet = new Set(visibleIds);
      const fullIds = (context.products || []).map((p) => Number(p.id)).filter(Boolean);
      const mergedIds = [...visibleIds, ...fullIds.filter((id) => !visibleSet.has(id))];
      const { data } = await api.put("/api/admin/product-display-order", {
        placement: "rental",
        store_category: storeCategory,
        product_ids: mergedIds,
      });
      setAdminOrderToast(`${storeCategory} ${data.saved || sectionItems.length}개 진열 순서 저장 완료`);
      setAdminOrderDirty(false);
      fetchProducts();
    } catch (e) {
      setAdminOrderToast(e.response?.data?.error || "진열 순서 저장 실패");
      if (e.response?.status === 409) fetchProducts();
    } finally {
      setAdminOrderSavingKey("");
    }
  };
  const adminOrderSaveBlocked = Boolean(searchInput || searchQuery || filterColor || filterBrand || filterSort || filterCategory);

  /* ── Current region data ── */
  const currentRegionData = useMemo(
    () => KOREA_REGIONS.find((r) => r.short === selectedRegion),
    [selectedRegion]
  );

  const getDistrictCount = useCallback(
    (regionShort, districtName) =>
      districtCounts[getDistrictKey(regionShort, districtName)] ?? districtCounts[districtName] ?? 0,
    [districtCounts]
  );

  // 시/도 카드의 제품 수 = 시도 단위(전체) 페이지 카운트. 구/군 카운트를 합산하면
  // 같은 카탈로그가 구 수만큼 부풀려지므로(예: 25×17), 시도 카운트를 쓰고
  // 없을 때만 구/군 최댓값으로 근사한다 → 클릭 후 보이는 목록 수와 일치.
  const getRegionCount = useCallback(
    (regionShort, districts) => {
      const top = districtCounts[regionShort];
      if (typeof top === "number" && top > 0) return top;
      return (districts || []).reduce(
        (max, d) => Math.max(max, getDistrictCount(regionShort, d.name)),
        0
      );
    },
    [districtCounts, getDistrictCount]
  );

  const sortedDistricts = useMemo(() => {
    if (!currentRegionData) return [];
    if (!userLocation) return currentRegionData.districts;
    return [...currentRegionData.districts].sort(
      (a, b) =>
        getDistance(userLocation.lat, userLocation.lng, a.lat, a.lng) -
        getDistance(userLocation.lat, userLocation.lng, b.lat, b.lng)
    );
  }, [currentRegionData, userLocation]);

  useEffect(() => {
    if (!selectedDistrict || !activeDistrictRef.current) return;
    const tid = setTimeout(() => {
      activeDistrictRef.current?.scrollIntoView({ block: "center", inline: "nearest" });
    }, 0);
    return () => clearTimeout(tid);
  }, [selectedDistrict, sortedDistricts]);

  /* ── SEO ── */
  const districtLabel = selectedDistrict
    ? getQualifiedDistrictLabel(selectedRegion, selectedDistrict)
    : "";
  const label = selectedDistrict
    ? districtLabel
    : selectedRegion || "전국";
  const seoTitle = selectedDistrict
    ? `${districtLabel} 복합기 렌탈 - ${BRAND.name} | ${districtLabel} 프린터 임대`
    : isNationwideRental || !selectedRegion
    ? `전국 복합기 렌탈 - ${BRAND.name} | 지역별 프린터 임대 전문`
    : selectedRegion
    ? `${selectedRegion} 복합기 렌탈 - ${BRAND.name} | ${selectedRegion} 지역 프린터 임대`
    : `전국 복합기 렌탈 - ${BRAND.name} | 지역별 프린터 임대 전문`;
  const seoDesc = selectedDistrict
    ? `${districtLabel} 복합기 렌탈, 프린터 임대 전문. 월 3만원부터 삼성·캐논·후지 복합기 렌탈. ${districtLabel} 무료 설치 및 A/S. 견적 문의 1600-7542`
    : isNationwideRental || !selectedRegion
    ? "전국 지역별 복합기 렌탈, 프린터 임대 전문. 서울·경기·부산·대구·인천 등 전국 어디서나 무료 설치. 견적 문의 1600-7542"
    : selectedRegion
    ? `${selectedRegion} 지역 복합기 렌탈, 프린터 임대 전문. ${selectedRegion} 전 지역 무료 설치 및 정기 점검. 견적 문의 1600-7542`
    : "전국 지역별 복합기 렌탈, 프린터 임대 전문. 서울·경기·부산·대구·인천 등 전국 어디서나 무료 설치. 견적 문의 1600-7542";
  const seoKeywords = [
    selectedDistrict && `${districtLabel} 복합기 렌탈`,
    selectedDistrict && `${districtLabel} 프린터 임대`,
    selectedDistrict && `${selectedDistrict} 복합기 렌탈`,
    selectedRegion && `${selectedRegion} 복합기 렌탈`,
    "복합기 렌탈",
    "프린터 임대",
    "복사기 렌탈",
    "사무용 복합기",
    BRAND.name,
  ]
    .filter(Boolean)
    .join(", ");

  const canonicalPath = selectedDistrict
    ? `/rental/${encodeURIComponent(selectedRegion)}/${encodeURIComponent(selectedDistrict)}`
    : selectedRegion
    ? `/rental/${encodeURIComponent(selectedRegion)}`
    : "/rental";
  const inquiryArea = districtLabel || (isNationwideRental ? "" : selectedRegion || "");

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: BRAND.name,
    description: seoDesc,
    telephone: "1600-7542",
    url: `${BRAND.url}${canonicalPath}`,
    address: {
      "@type": "PostalAddress",
      addressLocality: districtLabel || selectedRegion || "서울",
      addressRegion: currentRegionData?.region || "대한민국",
      addressCountry: "KR",
    },
    areaServed: label,
    priceRange: "₩30,000~",
    openingHours: "Mo-Fr 10:00-18:00",
  };

  /* ── Region SVG landmark icons ── */
  const REGION_SVG = {
    서울: (
      <svg viewBox="0 0 64 64" className="w-full h-full"><defs><linearGradient id="g-seoul" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#6366f1"/><stop offset="100%" stopColor="#818cf8"/></linearGradient></defs>
        <rect width="64" height="64" rx="16" fill="url(#g-seoul)"/>
        <path d="M32 12l-2 6h-6l5 4-2 6 5-4 5 4-2-6 5-4h-6z" fill="#fff" opacity=".9"/>
        <rect x="18" y="30" width="28" height="22" rx="2" fill="#fff" opacity=".85"/>
        <rect x="22" y="34" width="6" height="6" rx="1" fill="#6366f1" opacity=".5"/>
        <rect x="36" y="34" width="6" height="6" rx="1" fill="#6366f1" opacity=".5"/>
        <rect x="28" y="42" width="8" height="10" rx="1" fill="#6366f1" opacity=".6"/>
      </svg>
    ),
    부산: (
      <svg viewBox="0 0 64 64" className="w-full h-full"><defs><linearGradient id="g-busan" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#0ea5e9"/><stop offset="100%" stopColor="#38bdf8"/></linearGradient></defs>
        <rect width="64" height="64" rx="16" fill="url(#g-busan)"/>
        <path d="M8 38c6-4 12 4 18 0s12 4 18 0 12 4 12 0v18H8z" fill="#fff" opacity=".3"/>
        <path d="M8 42c6-3 12 3 18 0s12 3 18 0 12 3 12 0v14H8z" fill="#fff" opacity=".5"/>
        <path d="M24 18l8-6 8 6v22H24z" fill="#fff" opacity=".85"/>
        <rect x="28" y="22" width="3" height="3" rx=".5" fill="#0ea5e9" opacity=".5"/>
        <rect x="33" y="22" width="3" height="3" rx=".5" fill="#0ea5e9" opacity=".5"/>
        <rect x="28" y="28" width="3" height="3" rx=".5" fill="#0ea5e9" opacity=".5"/>
        <rect x="33" y="28" width="3" height="3" rx=".5" fill="#0ea5e9" opacity=".5"/>
      </svg>
    ),
    대구: (
      <svg viewBox="0 0 64 64" className="w-full h-full"><defs><linearGradient id="g-daegu" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#dc2626"/><stop offset="100%" stopColor="#f87171"/></linearGradient></defs>
        <rect width="64" height="64" rx="16" fill="url(#g-daegu)"/>
        <circle cx="32" cy="26" r="8" fill="#fff" opacity=".85"/>
        <path d="M22 38c0-6 4.5-10 10-10s10 4 10 10v12H22z" fill="#fff" opacity=".7"/>
        <rect x="14" y="42" width="36" height="3" rx="1.5" fill="#fff" opacity=".5"/>
        <rect x="18" y="47" width="28" height="2" rx="1" fill="#fff" opacity=".3"/>
      </svg>
    ),
    인천: (
      <svg viewBox="0 0 64 64" className="w-full h-full"><defs><linearGradient id="g-incheon" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#7c3aed"/><stop offset="100%" stopColor="#a78bfa"/></linearGradient></defs>
        <rect width="64" height="64" rx="16" fill="url(#g-incheon)"/>
        <path d="M12 40l20-24 20 24z" fill="#fff" opacity=".15"/>
        <path d="M32 16l-5 3 1 8h8l1-8z" fill="#fff" opacity=".85"/>
        <path d="M22 27l10-3 10 3v5l-10 4-10-4z" fill="#fff" opacity=".6"/>
        <rect x="14" y="38" width="36" height="4" rx="2" fill="#fff" opacity=".35"/>
        <path d="M8 44c6-2 12 2 18 0s12 2 18 0 12 2 12 0v12H8z" fill="#fff" opacity=".35"/>
      </svg>
    ),
    광주: (
      <svg viewBox="0 0 64 64" className="w-full h-full"><defs><linearGradient id="g-gwangju" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#16a34a"/><stop offset="100%" stopColor="#4ade80"/></linearGradient></defs>
        <rect width="64" height="64" rx="16" fill="url(#g-gwangju)"/>
        <circle cx="32" cy="24" r="10" fill="#fff" opacity=".2"/>
        <path d="M32 14c-3 0-5 2-5 4 0 3 5 10 5 10s5-7 5-10c0-2-2-4-5-4z" fill="#fff" opacity=".85"/>
        <circle cx="32" cy="18" r="2" fill="#16a34a" opacity=".5"/>
        <path d="M18 38c4-6 10-2 14-6s10 0 14-2v18H18z" fill="#fff" opacity=".4"/>
        <circle cx="22" cy="44" r="3" fill="#fff" opacity=".3"/>
        <circle cx="42" cy="42" r="4" fill="#fff" opacity=".25"/>
      </svg>
    ),
    대전: (
      <svg viewBox="0 0 64 64" className="w-full h-full"><defs><linearGradient id="g-daejeon" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#2563eb"/><stop offset="100%" stopColor="#60a5fa"/></linearGradient></defs>
        <rect width="64" height="64" rx="16" fill="url(#g-daejeon)"/>
        <circle cx="32" cy="28" r="12" fill="#fff" opacity=".15"/>
        <ellipse cx="32" cy="28" rx="5" ry="8" fill="#fff" opacity=".8"/>
        <ellipse cx="32" cy="28" rx="8" ry="5" fill="#fff" opacity=".5"/>
        <circle cx="32" cy="28" r="3" fill="#2563eb" opacity=".3"/>
        <rect x="20" y="44" width="24" height="6" rx="3" fill="#fff" opacity=".4"/>
      </svg>
    ),
    울산: (
      <svg viewBox="0 0 64 64" className="w-full h-full"><defs><linearGradient id="g-ulsan" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#ea580c"/><stop offset="100%" stopColor="#fb923c"/></linearGradient></defs>
        <rect width="64" height="64" rx="16" fill="url(#g-ulsan)"/>
        <rect x="16" y="28" width="8" height="22" rx="1" fill="#fff" opacity=".7"/>
        <rect x="28" y="22" width="8" height="28" rx="1" fill="#fff" opacity=".85"/>
        <rect x="40" y="32" width="8" height="18" rx="1" fill="#fff" opacity=".6"/>
        <circle cx="20" cy="24" r="2" fill="#fff" opacity=".5"/>
        <circle cx="32" cy="18" r="2" fill="#fff" opacity=".5"/>
        <circle cx="44" cy="28" r="2" fill="#fff" opacity=".5"/>
      </svg>
    ),
    세종: (
      <svg viewBox="0 0 64 64" className="w-full h-full"><defs><linearGradient id="g-sejong" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#0891b2"/><stop offset="100%" stopColor="#22d3ee"/></linearGradient></defs>
        <rect width="64" height="64" rx="16" fill="url(#g-sejong)"/>
        <rect x="20" y="24" width="24" height="26" rx="3" fill="#fff" opacity=".85"/>
        <rect x="24" y="28" width="7" height="5" rx="1" fill="#0891b2" opacity=".4"/>
        <rect x="33" y="28" width="7" height="5" rx="1" fill="#0891b2" opacity=".4"/>
        <rect x="28" y="38" width="8" height="12" rx="1" fill="#0891b2" opacity=".5"/>
        <path d="M18 24l14-10 14 10" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" opacity=".8"/>
      </svg>
    ),
    경기: (
      <svg viewBox="0 0 64 64" className="w-full h-full"><defs><linearGradient id="g-gyeonggi" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#059669"/><stop offset="100%" stopColor="#34d399"/></linearGradient></defs>
        <rect width="64" height="64" rx="16" fill="url(#g-gyeonggi)"/>
        <rect x="12" y="30" width="14" height="20" rx="2" fill="#fff" opacity=".8"/>
        <rect x="15" y="33" width="4" height="3" rx=".5" fill="#059669" opacity=".4"/>
        <rect x="15" y="38" width="4" height="3" rx=".5" fill="#059669" opacity=".4"/>
        <rect x="30" y="24" width="14" height="26" rx="2" fill="#fff" opacity=".9"/>
        <rect x="33" y="27" width="4" height="3" rx=".5" fill="#059669" opacity=".4"/>
        <rect x="33" y="32" width="4" height="3" rx=".5" fill="#059669" opacity=".4"/>
        <rect x="33" y="37" width="4" height="3" rx=".5" fill="#059669" opacity=".4"/>
        <rect x="46" y="34" width="8" height="16" rx="2" fill="#fff" opacity=".6"/>
      </svg>
    ),
    강원: (
      <svg viewBox="0 0 64 64" className="w-full h-full"><defs><linearGradient id="g-gangwon" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#0d9488"/><stop offset="100%" stopColor="#5eead4"/></linearGradient></defs>
        <rect width="64" height="64" rx="16" fill="url(#g-gangwon)"/>
        <path d="M8 44l12-22 10 14 8-18 12 16 8-10v20H8z" fill="#fff" opacity=".3"/>
        <path d="M8 48l14-18 10 10 8-14 10 12 8-6v12H8z" fill="#fff" opacity=".6"/>
        <circle cx="38" cy="18" r="4" fill="#fff" opacity=".8"/>
        <path d="M36 22l2 6 2-6" fill="#fff" opacity=".5"/>
      </svg>
    ),
    충북: (
      <svg viewBox="0 0 64 64" className="w-full h-full"><defs><linearGradient id="g-chungbuk" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#7c3aed"/><stop offset="100%" stopColor="#c084fc"/></linearGradient></defs>
        <rect width="64" height="64" rx="16" fill="url(#g-chungbuk)"/>
        <path d="M10 48l14-24 12 16 10-20 12 18v6H10z" fill="#fff" opacity=".35"/>
        <path d="M10 50l16-16 10 10 12-14 10 12v8H10z" fill="#fff" opacity=".6"/>
        <circle cx="24" cy="20" r="3" fill="#fff" opacity=".7"/>
        <circle cx="42" cy="16" r="2" fill="#fff" opacity=".5"/>
      </svg>
    ),
    충남: (
      <svg viewBox="0 0 64 64" className="w-full h-full"><defs><linearGradient id="g-chungnam" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#ca8a04"/><stop offset="100%" stopColor="#fbbf24"/></linearGradient></defs>
        <rect width="64" height="64" rx="16" fill="url(#g-chungnam)"/>
        <path d="M16 30c2-8 6-4 8-10s6 2 8-4 6 6 8 0 6 8 8 4v30H16z" fill="#fff" opacity=".5"/>
        <ellipse cx="32" cy="48" rx="16" ry="4" fill="#fff" opacity=".3"/>
        <path d="M26 24l6-8 6 8-2 14h-8z" fill="#fff" opacity=".75"/>
      </svg>
    ),
    전북: (
      <svg viewBox="0 0 64 64" className="w-full h-full"><defs><linearGradient id="g-jeonbuk" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#e11d48"/><stop offset="100%" stopColor="#fb7185"/></linearGradient></defs>
        <rect width="64" height="64" rx="16" fill="url(#g-jeonbuk)"/>
        <circle cx="32" cy="28" r="10" fill="#fff" opacity=".25"/>
        <path d="M32 18c-2 0-4 2-4 5 0 4 4 9 4 9s4-5 4-9c0-3-2-5-4-5z" fill="#fff" opacity=".85"/>
        <path d="M28 36c-6 2-10 4-10 8v6h28v-6c0-4-4-6-10-8l-4 3z" fill="#fff" opacity=".5"/>
        <path d="M24 42h16v4H24z" fill="#fff" opacity=".3"/>
      </svg>
    ),
    전남: (
      <svg viewBox="0 0 64 64" className="w-full h-full"><defs><linearGradient id="g-jeonnam" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#db2777"/><stop offset="100%" stopColor="#f472b6"/></linearGradient></defs>
        <rect width="64" height="64" rx="16" fill="url(#g-jeonnam)"/>
        <circle cx="22" cy="30" r="3" fill="#fff" opacity=".5"/>
        <circle cx="32" cy="26" r="4" fill="#fff" opacity=".6"/>
        <circle cx="42" cy="32" r="3" fill="#fff" opacity=".4"/>
        <path d="M8 40c6-3 12 2 18-1s12 3 18 0 12 2 12 0v16H8z" fill="#fff" opacity=".4"/>
        <path d="M8 46c6-2 12 2 18 0s12 2 18-1 12 3 12 1v10H8z" fill="#fff" opacity=".6"/>
      </svg>
    ),
    경북: (
      <svg viewBox="0 0 64 64" className="w-full h-full"><defs><linearGradient id="g-gyeongbuk" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#b91c1c"/><stop offset="100%" stopColor="#fca5a5"/></linearGradient></defs>
        <rect width="64" height="64" rx="16" fill="url(#g-gyeongbuk)"/>
        <rect x="20" y="26" width="24" height="24" rx="3" fill="#fff" opacity=".8"/>
        <path d="M20 26l12-12 12 12" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" opacity=".8"/>
        <rect x="24" y="30" width="5" height="5" rx="1" fill="#b91c1c" opacity=".35"/>
        <rect x="35" y="30" width="5" height="5" rx="1" fill="#b91c1c" opacity=".35"/>
        <rect x="28" y="38" width="8" height="12" rx="1.5" fill="#b91c1c" opacity=".4"/>
      </svg>
    ),
    경남: (
      <svg viewBox="0 0 64 64" className="w-full h-full"><defs><linearGradient id="g-gyeongnam" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#1d4ed8"/><stop offset="100%" stopColor="#93c5fd"/></linearGradient></defs>
        <rect width="64" height="64" rx="16" fill="url(#g-gyeongnam)"/>
        <path d="M14 48l8-20 8 12 8-16 8 10 6-8v18H14z" fill="#fff" opacity=".4"/>
        <path d="M8 44c8-3 16 3 24 0s16 3 24 0v14H8z" fill="#fff" opacity=".5"/>
        <rect x="26" y="20" width="12" height="18" rx="2" fill="#fff" opacity=".8"/>
        <rect x="29" y="23" width="3" height="3" rx=".5" fill="#1d4ed8" opacity=".4"/>
        <rect x="34" y="23" width="3" height="3" rx=".5" fill="#1d4ed8" opacity=".4"/>
      </svg>
    ),
    제주: (
      <svg viewBox="0 0 64 64" className="w-full h-full"><defs><linearGradient id="g-jeju" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#f97316"/><stop offset="100%" stopColor="#fdba74"/></linearGradient></defs>
        <rect width="64" height="64" rx="16" fill="url(#g-jeju)"/>
        <ellipse cx="32" cy="44" rx="22" ry="8" fill="#fff" opacity=".4"/>
        <path d="M32 14l-8 24h16z" fill="#fff" opacity=".7"/>
        <ellipse cx="32" cy="14" rx="5" ry="3" fill="#fff" opacity=".45"/>
        <path d="M18 38c4 2 8 0 14 2s10-2 14 0" fill="none" stroke="#fff" strokeWidth="1.5" opacity=".5"/>
      </svg>
    ),
  };

  return (
    <div ref={revealRef} className="min-h-screen bg-[#f5f5f7]">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <meta name="keywords" content={seoKeywords} />
        <link rel="canonical" href={`${BRAND.url}${canonicalPath}`} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${BRAND.url}${canonicalPath}`} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* ── SEO Content (unique per region/district, matches bot-rendered HTML) ── */}
      {seoContent && (
        <section className="bg-white border-b border-gray-100">
          <div className="max-w-[1200px] mx-auto px-5 py-6 md:py-8">
            <p className="text-[11px] font-semibold tracking-widest uppercase text-[#0a6bff] mb-1">
              {seoContent.kicker}
            </p>
            <h1 className="text-[20px] md:text-[26px] font-bold text-[#1d1d1f] mb-3">
              {seoContent.h1}
            </h1>
            <p className="text-[13px] md:text-[14px] text-[#3a3a3c] leading-relaxed mb-2">
              {seoContent.intro}
            </p>
            <p className="text-[13px] md:text-[14px] text-[#3a3a3c] leading-relaxed">
              {seoContent.intro2}
            </p>
          </div>
        </section>
      )}

      {/* ── Location Prompt ── */}
      {showLocationPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-4 p-7 text-center">
            <div className="w-14 h-14 mx-auto mb-4 bg-blue-50 rounded-full flex items-center justify-center">
              <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" /></svg>
            </div>
            <h3 className="text-lg font-bold text-[#1d1d1f] mb-2">{t.rental_locationTitle}</h3>
            <p className="text-sm text-gray-500 mb-6 leading-relaxed">
              {t.rental_locationDesc}
            </p>
            <div className="flex gap-3">
              <button onClick={dismissLocation} className="flex-1 py-2.5 rounded-xl border border-gray-300 text-sm font-medium text-gray-600 hover:bg-gray-50 transition">
                {t.rental_later}
              </button>
              <button onClick={requestLocation} className="flex-1 py-2.5 rounded-xl bg-[#0071e3] text-white text-sm font-semibold hover:bg-blue-700 transition">
                {t.rental_allowLocation}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Dark Hero Banner ── */}
      <section className="relative bg-gradient-to-b from-[#0a0a12] to-[#1a1a2e] text-white min-h-[380px] flex items-center">
        <div className="max-w-5xl mx-auto px-6 pt-24 pb-16 text-center w-full">
          <h1 className="text-3xl md:text-[44px] font-bold tracking-tight leading-tight">
            {selectedDistrict ? (
              <><span className="text-[#60a5fa]">{districtLabel}</span> {t.rental_copierRental}</>
            ) : isNationwideRental ? (
              t.rental_nationwideCopierRental
            ) : selectedRegion ? (
              <><span className="text-[#60a5fa]">{selectedRegion}</span> {t.rental_regionCopierRental}</>
            ) : (
              t.rental_nationwideCopierRental
            )}
          </h1>
          <p className="text-[16px] text-white/70 mt-3 max-w-xl mx-auto">
            {selectedDistrict
              ? `${districtLabel} ${t.rental_districtDesc}`
              : isNationwideRental
              ? t.rental_nationwideDesc
              : selectedRegion
              ? `${selectedRegion} ${t.rental_regionDesc}`
              : t.rental_nationwideDesc}
          </p>
          <div className="flex items-center justify-center gap-1.5 mt-3 text-xs text-white/40">
            <Link to="/rental" className={`hover:text-white/70 ${!selectedRegion ? "text-[#60a5fa] font-semibold" : ""}`}>{t.rental_all}</Link>
            {selectedRegion && (
              <>
                <span>›</span>
                <Link to={`/rental/${encodeURIComponent(selectedRegion)}`} className={`hover:text-white/70 ${!selectedDistrict ? "text-[#60a5fa] font-semibold" : ""}`}>
                  {selectedRegion}
                </Link>
              </>
            )}
            {selectedDistrict && (
              <>
                <span>›</span>
                <span className="text-[#60a5fa] font-semibold">{districtLabel}</span>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ── Location Bar (2-row, below banner) ── */}
      <div className="bg-white border-b border-gray-200 sticky top-12 z-30">
        <div className="max-w-5xl mx-auto px-6 py-2 text-[13px]">
          <div className="flex items-center justify-center gap-1.5 mb-1.5">
            {userRegionLabel ? (
              <button
                onClick={() => {
                  if (nearestDistrict) handleDistrictClick(nearestDistrict.regionShort, nearestDistrict.name);
                }}
                className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 font-medium hover:bg-blue-100 transition"
              >
                <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                {userRegionLabel}
              </button>
            ) : (
              <button onClick={requestLocation} className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-gray-100 text-gray-600 font-medium hover:bg-gray-200 transition">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" /></svg> {t.rental_myLocation}
              </button>
            )}
            <span className="text-gray-300">|</span>
            <button onClick={handleAllClick} className={`px-3 py-1 rounded-full font-medium transition ${!selectedRegion ? "bg-[#1d1d1f] text-white" : "text-gray-600 hover:bg-gray-100"}`}>
              {t.rental_all}
            </button>
            <button onClick={handleNationwideClick} className={`px-3 py-1 rounded-full font-medium transition ${isNationwideRental ? "bg-[#1d1d1f] text-white" : "text-gray-600 hover:bg-gray-100"}`}>
              전국
            </button>
            {KOREA_REGIONS.slice(0, 8).map((r) => (
              <button key={r.short} onClick={() => handleRegionClick(r.short)} className={`px-3 py-1 rounded-full font-medium transition ${selectedRegion === r.short ? "bg-[#1d1d1f] text-white" : "text-gray-600 hover:bg-gray-100"}`}>
                {r.short}
              </button>
            ))}
          </div>
          <div className="flex items-center justify-center gap-1.5">
            {KOREA_REGIONS.slice(8).map((r) => (
              <button key={r.short} onClick={() => handleRegionClick(r.short)} className={`px-3 py-1 rounded-full font-medium transition ${selectedRegion === r.short ? "bg-[#1d1d1f] text-white" : "text-gray-600 hover:bg-gray-100"}`}>
                {r.short}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 pt-6 pb-20">

        {/* ── VWorld 출장 서비스 커버리지 맵 ── */}
        {!selectedRegion && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-[13px] font-semibold text-gray-700">🚐 전국 출장 서비스 커버리지</p>
                <p className="text-[11px] text-gray-400 mt-0.5">{BRAND.name}는 전국 어디든 찾아갑니다 — 설치·점검·수거까지 원스톱</p>
              </div>
              <button onClick={() => setShowRentalMap(!showRentalMap)} className={`px-3 py-1.5 rounded-lg text-[12px] font-medium transition ${showRentalMap ? 'bg-[#1d1d1f] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {showRentalMap ? '접기' : '🗺️ 지도 보기'}
              </button>
            </div>
            {showRentalMap && (
              <>
                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm h-[280px] md:h-[400px]">
                  <MapContainer center={[36.3, 127.8]} zoom={7} style={{ width: '100%', height: '100%' }} zoomControl={false}>
                    <TileLayer url={VWORLD_BASE} maxZoom={19} attribution="© VWorld" />
                    <MapFlyTo center={mapFlyCenter} zoom={mapFlyZoom} />
                    {KOREA_REGIONS.map((r) => {
                      const total = getRegionCount(r.short, r.districts);
                      // Color by service tier: metro=blue, major=emerald, other=amber
                      const metro = ['서울', '경기', '인천'].includes(r.short);
                      const major = ['부산', '대구', '대전', '광주', '울산', '세종'].includes(r.short);
                      const color = metro ? '#0071e3' : major ? '#059669' : '#d97706';
                      const label = metro ? '당일 출장' : major ? '1~2일 출장' : '2~3일 출장';
                      return (
                        <CircleMarker key={r.short} center={[r.lat, r.lng]}
                          radius={metro ? 14 : major ? 11 : 9}
                          pathOptions={{ fillColor: color, color: '#fff', weight: 2, fillOpacity: 0.75 }}
                          eventHandlers={{ click: () => { handleRegionClick(r.short); setMapFlyCenter([r.lat, r.lng]); setMapFlyZoom(10); } }}
                        >
                          <Popup>
                            <div style={{ minWidth: 150, fontSize: 12 }}>
                              <p style={{ fontWeight: 700, fontSize: 13 }}>{r.region}</p>
                              <p style={{ color, fontWeight: 600, marginTop: 2 }}>🚐 {label}</p>
                              <p style={{ color: '#666', fontSize: 11, marginTop: 4 }}>무료 설치 · 정기 점검 · 수거</p>
                              {total > 0 && <p style={{ color: '#0071e3', fontSize: 11, marginTop: 2 }}>렌탈 가능 제품 {total}개</p>}
                            </div>
                          </Popup>
                        </CircleMarker>
                      );
                    })}
                  </MapContainer>
                </div>
                {/* Service tier legend */}
                <div className="flex items-center justify-between mt-3 px-1">
                  <div className="flex items-center gap-4 text-[11px]">
                    {[
                      ['#0071e3', '당일 출장', '수도권'],
                      ['#059669', '1~2일', '광역시'],
                      ['#d97706', '2~3일', '기타 지역'],
                    ].map(([c, label, sub]) => (
                      <div key={c} className="flex items-center gap-1.5">
                        <span className="w-3 h-3 rounded-full inline-block" style={{ background: c }} />
                        <span className="text-gray-700 font-medium">{label}</span>
                        <span className="text-gray-400">({sub})</span>
                      </div>
                    ))}
                  </div>
                  <span className="text-[10px] text-gray-300">VWorld 공간정보 기반</span>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Region Grid (no region selected) ── */}
        {/* scroll-reveal applied to region grid and product sections below */}
        {!selectedRegion && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-12">
              {KOREA_REGIONS.map((r) => {
                const totalProducts = getRegionCount(r.short, r.districts);
                return (
                  <button key={r.short} onClick={() => handleRegionClick(r.short)} className="group bg-white rounded-2xl border border-gray-200 p-5 text-left hover:shadow-lg hover:border-[#0071e3]/30 transition-all duration-200">
                    <div className="w-12 h-12 mb-2 rounded-xl overflow-hidden shadow-sm group-hover:scale-105 transition-transform">{REGION_SVG[r.short] || REGION_SVG["서울"]}</div>
                    <h3 className="text-[15px] font-bold text-[#1d1d1f] group-hover:text-[#0071e3] transition-colors">{r.short}</h3>
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      {r.districts.length} {t.rental_districts}
                      {totalProducts > 0 && <span className="text-[#0071e3]"> · {t.rental_products} {totalProducts}</span>}
                    </p>
                  </button>
                );
              })}
            </div>

            {/* All districts list grouped by region */}
            <div className="space-y-8">
              {KOREA_REGIONS.map((r) => (
                <div key={r.short}>
                  <div className="flex items-center gap-2 mb-3">
                    <h2 className="text-[15px] font-bold text-[#1d1d1f]">{r.region}</h2>
                    <span className="text-[11px] text-gray-400">{r.districts.length} {t.rental_districts}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {r.districts.map((d) => {
                      const cnt = getDistrictCount(r.short, d.name);
                      return (
                        <Link key={`${r.short}-${d.name}`} to={`/rental/${encodeURIComponent(r.short)}/${encodeURIComponent(d.name)}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-full text-[12px] text-gray-700 hover:border-[#0071e3] hover:text-[#0071e3] transition">
                          {d.name}
                          {cnt > 0 && <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full">{cnt}</span>}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── District + Products layout ── */}
        {selectedRegion && (
          <div className={isNationwideRental ? "relative" : "flex gap-8 relative"}>
            {/* Sidebar overlay (mobile) */}
            {!isNationwideRental && sidebarOpen && <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}
            {!isNationwideRental && (
            <aside className={`fixed lg:relative top-0 left-0 h-full lg:h-auto z-50 lg:z-auto w-[260px] lg:w-[240px] flex-shrink-0 transform transition-transform duration-200 ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"} bg-white lg:bg-transparent`}>
              <div className="sticky top-12 bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
                <div className="px-4 py-3 border-b border-gray-100 bg-[#f5f5f7]">
                  <div className="flex items-center justify-between">
                    <h2 className="text-[13px] font-bold text-[#1d1d1f] uppercase tracking-wide">{currentRegionData?.region || t.rental_selectRegion}</h2>
                    <div className="flex items-center gap-2">
                      {userLocation && <span className="text-[10px] text-green-600 bg-green-50 px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-0.5"><svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" /></svg>GPS</span>}
                      <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-gray-400 hover:text-gray-600 text-lg">✕</button>
                    </div>
                  </div>
                </div>
                <div className="px-2 pt-2">
                  <button onClick={() => { setSelectedDistrict(""); navigate(`/rental/${encodeURIComponent(selectedRegion)}`, { replace: true }); }} className={`w-full text-left px-3 py-2 rounded-lg text-[13px] font-medium transition ${!selectedDistrict ? "bg-[#0071e3] text-white" : "text-gray-700 hover:bg-gray-100"}`}>
                    {currentRegionData?.short} {t.rental_all}
                  </button>
                </div>
                <div className="px-2 pt-2 pb-3 max-h-[calc(100vh-200px)] overflow-y-auto scrollbar-thin">
                  {sortedDistricts.map((d) => {
                    const cnt = getDistrictCount(selectedRegion, d.name);
                    const isActiveDistrict = selectedDistrict === d.name;
                    return (
                      <button
                        key={d.name}
                        ref={isActiveDistrict ? activeDistrictRef : null}
                        onClick={() => handleDistrictClick(selectedRegion, d.name)}
                        className={`w-full text-left px-3 py-1.5 rounded-lg text-[12px] transition flex items-center justify-between group ${isActiveDistrict ? "bg-[#0071e3] text-white font-semibold" : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"}`}
                      >
                        <span className="truncate">{d.name}</span>
                        {cnt > 0 && <span className={`text-[10px] ml-1 px-1.5 py-0.5 rounded-full flex-shrink-0 ${isActiveDistrict ? "bg-white/20 text-white" : "bg-blue-50 text-blue-600"}`}>{cnt}</span>}
                      </button>
                    );
                  })}
                </div>
                {!userLocation && (
                  <div className="px-3 pb-3">
                    <button onClick={requestLocation} className="w-full flex items-center justify-center gap-2 py-2 rounded-xl bg-gray-100 text-gray-600 text-[12px] font-medium hover:bg-gray-200 transition">{t.rental_findByLocation}</button>
                  </div>
                )}
              </div>
            </aside>
            )}

            {/* Main Content */}
            <div className="flex-1 min-w-0">
              <div className="flex gap-3 mb-5">
                {!isNationwideRental && (
                <button onClick={() => setSidebarOpen(true)} className="lg:hidden flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition flex-shrink-0">
                  {districtLabel || currentRegionData?.short || t.rental_region}
                </button>
                )}
                <div className="flex-1 relative">
                  <input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder={`${label} ${t.rental_searchPlaceholder}`} className="w-full bg-white border border-gray-200 text-sm text-[#1d1d1f] placeholder-gray-400 rounded-xl px-4 py-2.5 pl-10 focus:outline-none focus:ring-2 focus:ring-[#0071e3]/30 transition" />
                  <svg className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" /></svg>
                </div>
              </div>

              {/* ── Filter Bar ── */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                {/* Color/Mono toggle */}
                <div className="flex bg-white border border-gray-200 rounded-lg overflow-hidden">
                  {[
                    { key: "", label: t.rental_filterAll },
                    { key: "color", label: t.rental_filterColor },
                    { key: "mono", label: t.rental_filterMono },
                  ].map(opt => (
                    <button key={opt.key} onClick={() => setFilterColor(opt.key)}
                      className={`px-3 py-1.5 text-[13px] font-medium transition ${filterColor === opt.key ? "bg-[#0071e3] text-white" : "text-gray-600 hover:bg-gray-50"}`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
                {/* Brand filter */}
                {availableFilters.brands.length > 0 && (
                  <select value={filterBrand} onChange={e => setFilterBrand(e.target.value)}
                    className="bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-[12px] text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0071e3]/30">
                    <option value="">{t.rental_brandAll}</option>
                    {availableFilters.brands.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                )}
                {/* Category filter */}
                {availableFilters.categories.length > 0 && (
                  <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
                    className="bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-[12px] text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0071e3]/30">
                    <option value="">{t.rental_categoryAll}</option>
                    {availableFilters.categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                )}
                {/* Sort */}
                <select value={filterSort} onChange={e => setFilterSort(e.target.value)}
                  className="bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-[12px] text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0071e3]/30">
                  <option value="">{t.rental_sortDefault}</option>
                  <option value="price_asc">{t.rental_sortPriceAsc}</option>
                  <option value="price_desc">{t.rental_sortPriceDesc}</option>
                  <option value="name">{t.rental_sortName}</option>
                </select>
                {/* Clear filters */}
                {(filterColor || filterBrand || filterSort || filterCategory) && (
                  <button onClick={() => { setFilterColor(""); setFilterBrand(""); setFilterSort(""); setFilterCategory(""); }}
                    className="px-3 py-1.5 text-[12px] text-red-500 hover:text-red-700 font-medium transition">
                    {t.rental_reset}
                  </button>
                )}
              </div>

              <div className="flex items-center justify-between gap-3 mb-4 px-1 flex-wrap">
                <p className="text-sm text-gray-500">
                  {(selectedDistrict || selectedRegion) && <span className="text-[#0071e3] font-semibold">{districtLabel || selectedRegion}</span>}{" "}
                  {t.rental_rentalProducts} <span className="text-[#1d1d1f] font-bold">{products.length}</span>{t.rental_count}
                </p>
                <div className="flex items-center gap-2">
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={handleAdminOrderToggle}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${adminOrderMode ? "bg-[#1d1d1f] text-white hover:bg-gray-700" : "bg-blue-50 text-[#0071e3] hover:bg-blue-100"}`}
                    >
                      {adminOrderMode ? "진열 편집 종료" : "진열 편집"}
                    </button>
                  )}
                  <Link to={`/inquiry?district=${encodeURIComponent(inquiryArea)}`} className="text-sm text-[#0071e3] font-medium hover:underline">{t.rental_quoteInquiry}</Link>
                </div>
              </div>
              {isAdmin && adminOrderMode && (
                <div className="mb-4 rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-[12px] text-blue-900">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <p>
                      <span className="font-bold">관리자 진열 편집 모드</span>
                      <span className="text-blue-700"> · 상품을 드래그하거나 위/아래 버튼으로 옮긴 뒤 섹션별 저장을 누르세요.</span>
                    </p>
                    {adminOrderDirty && <span className="font-bold text-blue-700">저장 안 된 변경 있음</span>}
                  </div>
                  {adminOrderSaveBlocked && (
                    <p className="mt-2 text-amber-700">검색, 필터, 정렬이 적용된 상태에서는 저장을 잠시 막아두었습니다. 초기화 후 순서를 저장해주세요.</p>
                  )}
                  {adminOrderToast && (
                    <p className="mt-2 font-semibold text-blue-700">{adminOrderToast}</p>
                  )}
                </div>
              )}

              {loading ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                  {[...Array(6)].map((_, i) => <div key={i} className="animate-pulse bg-white rounded-2xl h-64 border border-gray-100" />)}
                </div>
              ) : products.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
                  <div className="mb-4"><svg className="w-12 h-12 text-gray-300 mx-auto" fill="none" stroke="currentColor" strokeWidth={1} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" /></svg></div>
                  <h3 className="text-lg font-bold text-[#1d1d1f] mb-2">
                    {selectedDistrict
                      ? `${districtLabel} ${t.rental_noProductsYet}`
                      : selectedRegion
                      ? `${selectedRegion} ${t.rental_noProductsYet}`
                      : t.rental_selectDistrict}
                  </h3>
                  <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto leading-relaxed">
                    {selectedDistrict
                      ? `${districtLabel} ${t.rental_requestQuoteDesc}`
                      : t.rental_selectRegionHint}
                  </p>
                  <div className="flex gap-3 justify-center flex-wrap">
                    <Link to={`/inquiry?district=${encodeURIComponent(inquiryArea)}`} className="inline-flex items-center gap-2 px-6 py-3 bg-[#0071e3] text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition">{t.rental_requestQuote}</Link>
                    <a href="tel:1600-7542" className="inline-flex items-center gap-2 px-6 py-3 bg-[#1d1d1f] text-white rounded-xl text-sm font-semibold hover:bg-gray-700 transition">1600-7542</a>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10 max-w-lg mx-auto text-left">
                    <div className="bg-[#f5f5f7] rounded-xl p-4">
                      <div className="mb-1"><svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" /></svg></div>
                      <p className="text-xs font-bold text-[#1d1d1f]">{t.rental_freeInstall}</p>
                      <p className="text-[11px] text-gray-500 mt-0.5">{t.rental_freeInstallDesc}</p>
                    </div>
                    <div className="bg-[#f5f5f7] rounded-xl p-4">
                      <div className="mb-1"><svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17l-5.1-5.1a2.25 2.25 0 013.18-3.18l.72.72.72-.72a2.25 2.25 0 013.18 3.18L11.42 15.17z" /></svg></div>
                      <p className="text-xs font-bold text-[#1d1d1f]">{t.rental_regularCheck}</p>
                      <p className="text-[11px] text-gray-500 mt-0.5">{t.rental_regularCheckDesc}</p>
                    </div>
                    <div className="bg-[#f5f5f7] rounded-xl p-4">
                      <div className="mb-1"><svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5m8.25 3v6.75m0 0l-3-3m3 3l3-3M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" /></svg></div>
                      <p className="text-xs font-bold text-[#1d1d1f]">{t.rental_freeToner}</p>
                      <p className="text-[11px] text-gray-500 mt-0.5">{t.rental_freeTonerDesc}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {/* ── Category-grouped product grid ── */}
                  {(() => {
                    const sourceProducts = adminOrderMode ? adminOrderProducts : products;
                    const getStoreCategory = (p) => (p.store_category || "").trim();
                    const RENTAL_CATEGORIES = [
                      { key: "high_speed", label: "고속인쇄기", storeCategory: "고속인쇄기", match: (p) => {
                        const storeCategory = getStoreCategory(p);
                        if (storeCategory) return storeCategory === "고속인쇄기";
                        const n = `${p.name || ""} ${p.model_name || ""}`.toLowerCase();
                        return /c8180|c7580|c6580/i.test(n) || (p.category || "").includes("고속인쇄");
                      }},
                      { key: "design", label: "디자인용 복합기", storeCategory: "디자인용복합기", match: (p) => {
                        const storeCategory = getStoreCategory(p);
                        if (storeCategory) return storeCategory === "디자인용복합기";
                        const cat = (p.category || "").toLowerCase();
                        const n = `${p.name || ""} ${p.model_name || ""}`.toLowerCase();
                        return cat.includes("디자인") || cat.includes("design") || /c7071|c6571|c5571|c4571/i.test(n);
                      }},
                      { key: "office", label: "사무용 복합기", storeCategory: "사무용복합기", match: (p) => getStoreCategory(p) === "사무용복합기" || !getStoreCategory(p) },
                      { key: "small", label: "소형 복합기", storeCategory: "소형복합기", match: (p) => getStoreCategory(p) === "소형복합기" },
                      { key: "printer", label: "프린터", storeCategory: "프린터", match: (p) => getStoreCategory(p) === "프린터" },
                    ];
                    const grouped = {};
                    const used = new Set();
                    RENTAL_CATEGORIES.forEach(cat => {
                      grouped[cat.key] = sourceProducts.filter(p => {
                        if (used.has(p.id)) return false;
                        const ok = cat.match(p);
                        if (ok) used.add(p.id);
                        return ok;
                      });
                    });
                    const uncategorized = sourceProducts.filter(p => !used.has(p.id));
                    const sections = RENTAL_CATEGORIES.filter(c => grouped[c.key].length > 0);
                    if (uncategorized.length > 0) {
                      sections.push({ key: "etc", label: "기타", storeCategory: getStoreCategory(uncategorized[0]) || "기타", items: uncategorized });
                    }

                    const sectionStoreCategory = (section, items) => section.storeCategory || getStoreCategory(items[0]) || "기타";
                    const renderSectionControls = (section, items) => {
                      if (!adminOrderMode) return <span className="text-xs text-gray-400">{items.length}개 제품</span>;
                      const storeCategory = sectionStoreCategory(section, items);
                      const saving = adminOrderSavingKey === storeCategory;
                      return (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-400">{items.length}개 제품</span>
                          <button
                            type="button"
                            onClick={() => saveAdminOrderSection(storeCategory, items)}
                            disabled={saving || adminOrderSaveBlocked}
                            className={`px-3 py-1.5 rounded-lg text-[12px] font-bold transition ${saving || adminOrderSaveBlocked ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-[#0071e3] text-white hover:bg-blue-700"}`}
                          >
                            {saving ? "저장 중" : "이 섹션 저장"}
                          </button>
                        </div>
                      );
                    };

                    const renderGrid = (items) => (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {items.map((p, idx) => {
                          const source = p.source || p._source || "ikoffices";
                          const card = (
                            <Link
                              key={`${source}-${p.id}-card`}
                              to={p.product_url || `/p/${p.slug}`}
                              draggable={false}
                              onClick={(e) => {
                                if (adminOrderMode) e.preventDefault();
                              }}
                              className="group block bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg hover:border-gray-300 transition-all duration-200"
                            >
                              <div className="aspect-square bg-[#fafafa] flex items-center justify-center p-4 relative overflow-hidden">
                                {p.image_url ? (
                                  <img src={p.image_url} alt={`${p.name} - ${districtLabel || selectedRegion} 복합기 렌탈`} className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                                ) : (
                                  <div><svg className="w-10 h-10 text-gray-300" fill="none" stroke="currentColor" strokeWidth={1} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m0 0a48.44 48.44 0 0110.5 0m-10.5 0V4.875c0-.621.504-1.125 1.125-1.125h8.25c.621 0 1.125.504 1.125 1.125v2.234" /></svg></div>
                                )}
                                <div className="absolute top-2 left-2 flex flex-col gap-1">
                                  {p.sale_type === "rental" && <span className="bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{t.rental_badge_rental}</span>}
                                  {p.sale_type === "both" && <span className="bg-purple-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{t.rental_badge_both}</span>}
                                  {p.district && <span className="bg-blue-100 text-blue-700 text-[10px] font-medium px-2 py-0.5 rounded-full inline-flex items-center gap-0.5"><svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" /></svg>{p.district}</span>}
                                </div>
                              </div>
                              <div className="p-3">
                                <p className="text-[11px] text-gray-400 mb-0.5 font-bold">{p.brand || p.category}</p>
                                <h3 className="text-[13px] font-bold text-[#1d1d1f] line-clamp-2 leading-snug mb-2 group-hover:text-[#0071e3] transition-colors">{p.name}</h3>
                                <div className="flex items-end justify-between">
                                  {p.rental_price > 0 ? (
                                    <p className="text-[14px] font-bold text-green-600">{t.rental_monthly} {Number(p.rental_price).toLocaleString()}{t.rental_won}</p>
                                  ) : p.price > 0 ? (
                                    <p className="text-[14px] font-bold text-[#1d1d1f]">{Number(p.price).toLocaleString()}{t.rental_won}</p>
                                  ) : (
                                    <p className="text-[13px] text-gray-400 font-bold">{t.rental_priceInquiry}</p>
                                  )}
                                  <span className="text-[11px] text-[#0071e3] font-medium opacity-0 group-hover:opacity-100 transition-opacity">{t.rental_detail}</span>
                                </div>
                              </div>
                            </Link>
                          );
                          if (!adminOrderMode) return <React.Fragment key={`${source}-${p.id}`}>{card}</React.Fragment>;
                          return (
                            <div
                              key={`${source}-${p.id}`}
                              draggable
                              onDragStart={() => setAdminOrderDragId(p.id)}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={() => {
                                const fromIndex = items.findIndex((item) => item.id === adminOrderDragId);
                                moveAdminOrderProduct(items, fromIndex, idx);
                                setAdminOrderDragId(null);
                              }}
                              onDragEnd={() => setAdminOrderDragId(null)}
                              className={`rounded-2xl border-2 bg-white transition ${adminOrderDragId === p.id ? "border-blue-300 opacity-70" : "border-blue-100 hover:border-[#0071e3]"}`}
                            >
                              <div className="flex items-center justify-between gap-2 rounded-t-2xl bg-blue-50 px-3 py-2 text-[11px] text-blue-800">
                                <span className="cursor-grab font-bold">순서 #{idx + 1}</span>
                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() => moveAdminOrderProduct(items, idx, idx - 1)}
                                    disabled={idx === 0}
                                    className="h-7 px-2 rounded-md bg-white font-bold text-blue-700 disabled:text-gray-300 disabled:cursor-not-allowed"
                                    aria-label={`${p.name} 위로 이동`}
                                  >
                                    위
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => moveAdminOrderProduct(items, idx, idx + 1)}
                                    disabled={idx === items.length - 1}
                                    className="h-7 px-2 rounded-md bg-white font-bold text-blue-700 disabled:text-gray-300 disabled:cursor-not-allowed"
                                    aria-label={`${p.name} 아래로 이동`}
                                  >
                                    아래
                                  </button>
                                </div>
                              </div>
                              {card}
                            </div>
                          );
                        })}
                      </div>
                    );

                    if (sections.length <= 1 && uncategorized.length === 0) {
                      const section = sections[0] || { key: "all", label: "상품", storeCategory: getStoreCategory(sourceProducts[0]) || "사무용복합기" };
                      return (
                        <div>
                          {adminOrderMode && (
                            <div className="flex items-center justify-between mb-4">
                              <h2 className="text-[20px] font-bold text-[#1d1d1f] tracking-tight">{section.label}</h2>
                              {renderSectionControls(section, sourceProducts)}
                            </div>
                          )}
                          {renderGrid(sourceProducts)}
                        </div>
                      );
                    }

                    return (
                      <div className="space-y-10">
                        {sections.map(cat => {
                          const items = cat.items || grouped[cat.key];
                          return (
                            <div key={cat.key}>
                              <div className="flex items-center justify-between mb-4">
                                <h2 className="text-[20px] font-bold text-[#1d1d1f] tracking-tight">{cat.label}</h2>
                                {renderSectionControls(cat, items)}
                              </div>
                              {renderGrid(items)}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </>
              )}

              {/* Bottom CTA */}
              <div className="mt-12">
                <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
                  <h3 className="text-lg font-bold text-[#1d1d1f] mb-2">{districtLabel || selectedRegion || t.rental_nationwide} {t.rental_copierConsult}</h3>
                  <p className="text-sm text-gray-500 mb-5">{t.rental_ctaDesc}</p>
                  <div className="flex gap-3 justify-center flex-wrap">
                    <Link to={`/inquiry?district=${encodeURIComponent(inquiryArea)}`} className="px-6 py-3 bg-[#0071e3] text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition">{t.rental_freeQuoteRequest}</Link>
                    <a href="tel:1600-7542" className="px-6 py-3 bg-[#1d1d1f] text-white rounded-xl text-sm font-semibold hover:bg-gray-700 transition">1600-7542</a>
                  </div>
                </div>
              </div>

              {/* SEO prose — unique FAQ per region/district */}
              {seoContent && seoContent.faq && seoContent.faq.length > 0 && (
                <div className="mt-10 max-w-none">
                  <h2 className="text-base font-bold text-gray-700 mb-4">
                    {seoContent.h1} · 자주 묻는 질문
                  </h2>
                  <dl className="space-y-4">
                    {seoContent.faq.map((item, i) => (
                      <div key={i} className="border-b border-gray-100 pb-3">
                        <dt className="text-[13px] font-semibold text-[#1d1d1f] mb-1">Q. {item.q}</dt>
                        <dd className="text-[13px] text-gray-600 leading-relaxed">A. {item.a}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
